import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Alert, Typography } from "@mui/material";
import PageNotFound from "./PageNotFoundPage";
import {
  QUERYPARAMS,
  QUERYPARAMSNAME,
} from "../layout/manualSwapLatest/common";
import { useAuth } from "../contexts/AuthContext";
import InventoryIcon from "@mui/icons-material/Inventory";
import LaptopMacIcon from "@mui/icons-material/LaptopMac";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import PageTitle from "../components/pageTitle";
import PageLayout from "./PageLayout";
import { InitiateSwap } from "../layout";
import { ROLES_TYPE } from "../constants/roles";
import AccessDeniedPage from "./AccessDeniedPage";

const SWAP_TYPE_CONFIG = {
  [QUERYPARAMS.INVENTORY]: {
    label: "Inventory",
    icon: <InventoryIcon sx={{ fontSize: 26 }} />,
    description: "Manage physical battery inventory swaps",
    featureKey: ROLES_TYPE.MANUAL_SWAP_INVENTORY,
    bgColor:
      "linear-gradient(90deg,rgb(37, 190, 190) 0%, #16a085 50%, #0f7a6b 100%)",
  },
  [QUERYPARAMS.DIGITAL_TWIN]: {
    label: "Digital Twin",
    icon: <LaptopMacIcon sx={{ fontSize: 26 }} />,
    description: "Handle digital twin battery swap operations",
    featureKey: ROLES_TYPE.MANUAL_SWAP_DIGITAL,
    bgColor: "linear-gradient(90deg, #1DA7A7 0%, #26d0ce 50%, #00b4d8 100%)",
  },
  [QUERYPARAMS.OTA]: {
    label: "OTA",
    icon: <CloudDownloadIcon sx={{ fontSize: 26 }} />,
    description: "Handle OTA battery swap operations",
    featureKey: ROLES_TYPE.MANUAL_SWAP_OTA,
    bgColor: "linear-gradient(90deg, #1DA7A7 0%, #48cae4 50%, #90e0ef 100%)",
  },
};

const SwapTypePage = () => {
  const { hasSubFeatureAccess, getAvailableSubFeatures } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const swapType = queryParams.get(QUERYPARAMSNAME.SWAP_TYPE);

  const availableSwapTypes = getAvailableSubFeatures(ROLES_TYPE.MANUAL_SWAP);

  const hasAnySwapAccess = availableSwapTypes.length > 0;

  const isValidSwapType =
    swapType && Object.keys(SWAP_TYPE_CONFIG).includes(swapType);
  const hasAccessToCurrentSwapType =
    swapType &&
    hasSubFeatureAccess(
      ROLES_TYPE.MANUAL_SWAP,
      SWAP_TYPE_CONFIG[swapType]?.featureKey
    );

  const handleNavigate = (swapType) => {
    navigate(`/manual-swap?${QUERYPARAMSNAME.SWAP_TYPE}=${swapType}`);
  };

  const unavailableOptions = Object.entries(SWAP_TYPE_CONFIG).filter(
    ([, config]) =>
      !hasSubFeatureAccess(ROLES_TYPE.MANUAL_SWAP, config.featureKey)
  );

  if (swapType) {
    if (!isValidSwapType) {
      return <PageNotFound />;
    }

    if (!hasAccessToCurrentSwapType) {
      return <AccessDeniedPage  />;
    }

    return (
      <PageLayout>
        <InitiateSwap />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageTitle title="Manual Swap" />

      {!hasAnySwapAccess ? (
        <Box sx={{ textAlign: "center", mt: 8 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            You don't have access to any manual swap operations. Please contact
            your administrator for access.
          </Alert>
          <Button variant="contained" onClick={() => navigate("/")}>
            Go to Dashboard
          </Button>
        </Box>
      ) : (
        <>
          <Typography
            variant="body1"
            sx={{
              mb: 3,
              textAlign: "left",
              fontWeight: 600,
              p: 1,
              pl: 4,
              borderRadius: 2,
              color: "#FFFFFF",
              background: {
                xs: "linear-gradient(45deg, #1DA7A7 60%, #FFFFFF 60%)",
                sm: "linear-gradient(45deg, #1DA7A7 50%, #FFFFFF 50%)",
                md: "linear-gradient(45deg, #1DA7A7 40%, #FFFFFF 40%)",
              },
              borderBottom: "1px solid #1DA7A7",
              borderBottomRightRadius: 0,
              lineHeight: "2",
            }}
          >
            Select Swap Type
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: `repeat(1, 100%)`,
                sm: `repeat(2, 48%)`,
                md: `repeat(2, 48%)`,
              },
              gap: 4,
              justifyContent: "space-between",
              mt: 5,
            }}
          >
            {Object.entries(SWAP_TYPE_CONFIG).map(([swapTypeKey, config]) => {
              const hasAccess = hasSubFeatureAccess(
                ROLES_TYPE.MANUAL_SWAP,
                config.featureKey
              );

              if (!hasAccess) return null;

              return (
                <SwapTypeCard
                  key={swapTypeKey}
                  config={config}
                  onClick={() => handleNavigate(swapTypeKey)}
                />
              );
            })}
          </Box>

          {unavailableOptions.length > 0 && (
            <Box sx={{ mt: 6 }}>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                sx={{ mb: 2 }}
              >
                Unavailable Options (Contact admin for access):
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {unavailableOptions.map(([swapTypeKey, config]) => (
                  <Box
                    key={swapTypeKey}
                    sx={{
                      padding: "8px 16px",
                      border: "1px dashed #ccc",
                      borderRadius: "4px",
                      color: "text.disabled",
                      backgroundColor: "#f9f9f9",
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                    }}
                  >
                    {config.icon} {config.label} (No Access)
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </>
      )}
    </PageLayout>
  );
};

const SwapTypeCard = ({ config, onClick }) => {
  return (
    <Button
      variant="contained"
      onClick={onClick}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        p: 3,
        minHeight: "120px",
        boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
        transition: "all 0.3s ease",
        borderRadius: 3,
        bgcolor: "#FFFFFF",
        border: "none",
        color: "#000000",
        textTransform: "none",
        borderLeft: "4px solid #1DA7A7",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0px 8px 20px rgba(0,0,0,0.2)",
          bgcolor: "#FFFFFF",
          border: "none",
          color: "#000000",
          borderLeft: "4px solid #1DA7A7",
        },
      }}
    >
      <Box
        sx={{
          width: 60,
          height: 60,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: config.bgColor,
          color: "#fff",
          fontSize: 32,
        }}
      >
        {config.icon}
      </Box>
      <Box sx={{ textAlign: "center" }}>
        <Typography
          variant="h6"
          fontWeight={600}
          gutterBottom
          sx={{ color: "#222" }}
        >
          {config.label}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, maxWidth: "90%", margin: "auto", fontSize: "0.8rem" }}
        >
          {config.description}
        </Typography>
      </Box>
    </Button>
  );
};

export default SwapTypePage;
